// src/components/Cenik.tsx
import React, { useMemo } from 'react';
import { motion, Variants } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { formatApproxEur } from '../utils/currency';

type SeasonKey = 'vanoce' | 'zimni1' | 'zimni2' | 'leto' | 'mimosezona';

type SeasonDef = {
  key: SeasonKey;
  icon: string;
  nameKey: string;
  color: string;
  pricePerPerson: number;
  pricePerPet: number;
  weekly: Record<number, number>;
  shortStay?: Record<number, number>;
};

const rawSeasons: SeasonDef[] = [
  {
    key: 'vanoce',
    icon: '🎄',
    nameKey: 'pricing.season.vanoce',
    color: 'bg-red-50',
    pricePerPerson: 750,
    pricePerPet: 150,
    weekly: { 6: 31500, 7: 36750, 8: 42000 },
  },
  {
    key: 'zimni2',
    icon: '❄️',
    nameKey: 'pricing.season.zimni2',
    color: 'bg-blue-50',
    pricePerPerson: 600,
    pricePerPet: 150,
    weekly: { 4: 16800, 5: 21000, 6: 25200, 7: 29400, 8: 33600 },
  },
  {
    key: 'zimni1',
    icon: '⛷️',
    nameKey: 'pricing.season.zimni1',
    color: 'bg-sky-50',
    pricePerPerson: 550,
    pricePerPet: 150,
    weekly: { 4: 15400, 5: 19250, 6: 23100, 7: 26950, 8: 30800 },
  },
  {
    key: 'leto',
    icon: '🌞',
    nameKey: 'pricing.season.leto',
    color: 'bg-yellow-50',
    pricePerPerson: 550,
    pricePerPet: 150,
    weekly: { 4: 15400, 5: 19250, 6: 23100, 7: 26950, 8: 30800 },
    shortStay: { 4: 6600, 5: 8250, 6: 9900, 7: 11550, 8: 13200 },
  },
  {
    key: 'mimosezona',
    icon: '🍂',
    nameKey: 'pricing.season.mimosezona',
    color: 'bg-amber-50',
    pricePerPerson: 500,
    pricePerPet: 150,
    weekly: { 4: 14000, 5: 17500, 6: 21000, 7: 24500, 8: 28000 },
    shortStay: { 4: 6000, 5: 7500, 6: 9000, 7: 10500, 8: 12000 },
  },
];

// Vrací klíč sezóny podle data
const getSeasonKeyForDate = (date: Date): SeasonKey => {
  const m = date.getMonth() + 1;
  const d = date.getDate();

  // Vánoce: 21.12 - 31.12 nebo 1.1 - 2.1
  if ((m === 12 && d >= 21) || (m === 1 && d <= 2)) return 'vanoce';
  // Zimní: 3.1 - 31.3, + 3.1 zahrnuto
  if ((m === 1 && d >= 3) || m === 2 || m === 3) return 'zimni2';
  // Letní: 1.6 - 31.8
  if (m >= 6 && m <= 8) return 'leto';
  // Mimosezóna: duben, květen
  if (m === 4 || m === 5) return 'mimosezona';
  // Podzimní: září - listopad, + 1.12 - 20.12
  if (m === 9 || m === 10 || m === 11 || (m === 12 && d <= 20)) return 'zimni1';
  // Fallback: pokud něco nepadne, podle pořadí
  return 'mimosezona';
};

const sectionVariant: Variants = {
  hidden: { opacity: 0, y: 6 },
  visible: (delay: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay, duration: 0.4, ease: 'easeOut' },
  }),
};

const Cenik: React.FC = () => {
  const { t, i18n } = useTranslation();
  const today = useMemo(() => new Date(), []);
  const currentSeasonKey = useMemo(() => getSeasonKeyForDate(today), [today]);

  const seasons = useMemo<SeasonDef[]>(() => {
    const current = rawSeasons.find(s => s.key === currentSeasonKey);
    const others = rawSeasons.filter(s => s.key !== currentSeasonKey);
    return current ? [current, ...others] : [...rawSeasons];
  }, [currentSeasonKey]);

  return (
    <div className="min-h-screen bg-[#f7f2ed] text-[#3f2b25]">
      <div className="max-w-6xl mx-auto px-6 py-20 space-y-10">
        <div className="text-center">
          <motion.h1
            className="text-4xl md:text-5xl font-extrabold mb-2"
            initial="hidden"
            animate="visible"
            variants={sectionVariant}
            custom={0}
          >
            {t('pricing.title')}
          </motion.h1>
          <motion.p
            className="max-w-2xl mx-auto text-lg text-gray-700"
            initial="hidden"
            animate="visible"
            variants={sectionVariant}
            custom={0.2}
          >
            {t('pricing.subtitle')}
          </motion.p>
        </div>

        {/* Badge vysvětlující */}
        <div className="flex justify-center">
          <div className="text-sm bg-[#4B2E2E] text-white px-4 py-2 rounded-full font-medium">
            {t('pricing.currentSeason')}: {t(seasons[0].nameKey)}
          </div>
        </div>

        {seasons.map((season, idx) => (
          <motion.div
            key={season.key}
            className={`rounded-2xl p-8 relative overflow-hidden shadow-lg ${season.color} ${season.key === currentSeasonKey ? 'ring-2 ring-[#4B2E2E]' : ''}`}
            initial="hidden"
            animate="visible"
            variants={sectionVariant}
            custom={0.1 + idx * 0.05}
          >
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Levá část: základní info */}
              <div className="flex-1">
                <div className="flex items-start gap-4 mb-3">
                  <div className="text-4xl">{season.icon}</div>
                  <div>
                    <h2 className="text-2xl font-bold">{t(season.nameKey)}</h2>
                    {season.key === 'vanoce' && (
                      <div className="mt-2 text-sm text-gray-700 font-medium">
                        {t('pricing.christmasMinPeople')}
                      </div>
                    )}
                    {season.key === currentSeasonKey && (
                      <div className="inline-block mt-1 text-xs font-semibold bg-[#4B2E2E] text-white px-3 py-1 rounded-full">
                        {t('pricing.currentBadge')}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-3 bg-white/90 backdrop-blur-md rounded-lg px-4 py-2 shadow-sm">
                    <div className="text-2xl">💰</div>
                    <div>
                      <div className="text-xs uppercase text-gray-500">{t('pricing.pricePerPerson')}</div>
                      <div className="font-semibold text-lg">
                        {season.pricePerPerson.toLocaleString()} {i18n.language === 'cs' ? 'Kč' : 'CZK'}
                        {i18n.language !== 'cs' && (
                          <div className="text-sm text-gray-600 mt-1">(~ {formatApproxEur(season.pricePerPerson, i18n.language)})</div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 bg-white/90 backdrop-blur-md rounded-lg px-4 py-2 shadow-sm">
                    <div className="text-2xl">🐶</div>
                    <div>
                      <div className="text-xs uppercase text-gray-500">{t('pricing.petPerNight')}</div>
                      <div className="font-semibold text-lg">
                        {season.pricePerPet.toLocaleString()} {i18n.language === 'cs' ? 'Kč' : 'CZK'}
                        {i18n.language !== 'cs' && (
                          <div className="text-sm text-gray-600 mt-1">(~ {formatApproxEur(season.pricePerPet, i18n.language)})</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pravá část: tabulky cen */}
              <div className="flex-1">
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-2">{t('pricing.weekly')}</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                    {Object.entries(season.weekly).map(([people, price]) => (
                      <div
                        key={`weekly-${people}`}
                        className="bg-white/90 backdrop-blur-md rounded-xl p-3 md:p-4 flex flex-col gap-1 shadow"
                      >
                        <div className="text-xs md:text-sm text-gray-500">{people} {t('pricing.people')}</div>
                        <div className="font-semibold text-base md:text-lg">
                          {Number(price).toLocaleString()} {i18n.language === 'cs' ? 'Kč' : 'CZK'} / {t('pricing.week')}
                        </div>
                        {i18n.language !== 'cs' && (
                          <div className="text-xs md:text-sm text-gray-600">(~ {formatApproxEur(Number(price), i18n.language)})</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                {season.shortStay && (
                  <div>
                    <h3 className="text-lg font-semibold mb-2">{t('pricing.short')}</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                      {Object.entries(season.shortStay).map(([people, price]) => (
                        <div
                          key={`short-${people}`}
                          className="bg-white/90 backdrop-blur-md rounded-xl p-3 md:p-4 flex flex-col gap-1 shadow"
                        >
                          <div className="text-xs md:text-sm text-gray-500">{people} {t('pricing.people')}</div>
                          <div className="font-semibold text-base md:text-lg">
                            {Number(price).toLocaleString()} {i18n.language === 'cs' ? 'Kč' : 'CZK'} / 3 {t('pricing.nights')}
                          </div>
                          {i18n.language !== 'cs' && (
                            <div className="text-xs md:text-sm text-gray-600">(~ {formatApproxEur(Number(price), i18n.language)})</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default Cenik;
